<script setup lang="ts">
import { LoadStatus } from "@/models/frontend-only/LoadStatus";
const app = useAppStore();
const breakpoints = [640, 768, 1024, 1280, 1536];

const props = defineProps<{
  src: string;
  alt: string;
  rounded?: boolean;
  fetchPriority?: string;
  transparent?: boolean;
  contain?: boolean; //assumed always cover
  noLazy?: boolean;
  noTransition?: boolean;
  placeholder?: boolean;
  placeholderIcon?: string;
  noPlaceholderIcon?: boolean;
  sizes?: string;

  restrictHeight?: number;
  restrictWidth?: number;
}>();

const { pixelRatio } = useDevicePixelRatio();

const cfSrcSet = computed(() => {
  if (correctedSrc.value.endsWith(".svg")) {
    return undefined;
  }

  const properties: { [key: string]: string } = {
    format: "auto",
    dpr: pixelRatio.value.toString(),
    fit: "scale-down",
  };

  if (props.restrictWidth) {
    properties["width"] = props.restrictWidth.toString();
  }
  if (props.restrictHeight) {
    properties["height"] = props.restrictHeight.toString();
  }

  if (props.restrictHeight && props.restrictWidth) {
    if (props.contain) {
      properties["fit"] = "cover";
    } else {
      properties["fit"] = "fill";
    }
  }

  const srcSetArray: string[] = [];

  if (props.restrictWidth) {
    properties["width"] = props.restrictWidth.toString();

    const p: string[] = [];
    Object.entries(properties).forEach(([key, value]) => {
      p.push(`${key}=${value}`);
    });

    srcSetArray.push(
      `/cdn-cgi/image/${p.join(",")}/${correctedSrc.value} ${props.restrictWidth}w`,
    );
  }

  breakpoints
    .filter(
      (bp) =>
        !props.restrictWidth ||
        (props.restrictWidth && bp < props.restrictWidth),
    )
    .forEach((bp) => {
      properties["width"] = bp.toString();

      const p: string[] = [];
      Object.entries(properties).forEach(([key, value]) => {
        p.push(`${key}=${value}`);
      });

      srcSetArray.push(
        `/cdn-cgi/image/${p.join(",")}/${correctedSrc.value} ${bp}w`,
      );
    });

  return srcSetArray.join(", ");
});

const correctedSrc = computed(() => {
  let src = adjustImageSrc(props.src);

  if (src.includes("mzstatic.com")) {
    const ext = app.webpSupported ? "webp" : "jpg";
    if (props.restrictWidth) {
      src = src.replace(
        /\/[^/]*$/,
        `/${props.restrictWidth}x${props.restrictWidth}bb.${ext}`,
      );
    } else {
      src = src.replace(/\/[^/]*$/, `/1000x1000bb.${ext}`);
    }
  }
  return src;
});

const cfSrc = computed(() => {
  if (correctedSrc.value.endsWith(".svg")) {
    return correctedSrc.value;
  }

  const properties: { [key: string]: string } = {
    format: "auto",
    dpr: pixelRatio.value.toString(),
    fit: "scale-down",
  };

  if (props.restrictWidth) {
    properties["width"] = props.restrictWidth.toString();
  } else {
    properties["width"] = breakpoints[breakpoints.length - 1].toString();
  }

  const p: string[] = [];
  Object.entries(properties).forEach(([key, value]) => {
    p.push(`${key}=${value}`);
  });

  return `/cdn-cgi/image/${p.join(",")}/${props.src}`;
});

const div = ref<HTMLDivElement>();
const errorLoading = ref(false);
const fallbackImg = computed(
  () =>
    import.meta.env.VITE_ENABLE_CF_IMAGE_OPTIMIZATION !== "1" ||
    !correctedSrc.value.includes("ccli.com") ||
    errorLoading.value,
);
const status = ref<LoadStatus>(LoadStatus.Loading);

const emits = defineEmits<{
  (e: "load", evt: Event): void;
  (e: "error", evt: Event): void;
}>();

function onLoad(event: Event) {
  emits("load", event);
  status.value = LoadStatus.Ready;
}
function onError(event: Event, fallbackImgFail?: boolean) {
  if (!fallbackImgFail) {
    errorLoading.value = true;
  } else {
    status.value = LoadStatus.Error;
    emits("error", event);
  }
}
</script>

<template>
  <div
    ref="div"
    class="image-wrapper"
    :class="{
      rounded: rounded,
      transparent: transparent,
      'image-loading': status === LoadStatus.Loading,
      'image-error': status === LoadStatus.Error,
      'image-ready': status === LoadStatus.Ready,
      'no-transition': noTransition,
    }"
  >
    <FAIcon
      v-if="status === LoadStatus.Error && !placeholder && !noPlaceholderIcon"
      :class="placeholderIcon || 'fa-light fa-image'"
    />

    <img
      v-if="!placeholder && !fallbackImg"
      :srcset="cfSrcSet"
      :src="cfSrc"
      :sizes="sizes"
      :fetchpriority="fetchPriority"
      class="image"
      :class="{
        contain: contain,
      }"
      :alt="alt"
      :loading="(!noLazy && 'lazy') || undefined"
      @load="onLoad"
      @error="onError"
    />

    <img
      v-else-if="fallbackImg"
      :src="correctedSrc"
      :fetchpriority="fetchPriority"
      class="image image-ready"
      :class="{
        contain: contain,
      }"
      :alt="alt"
      :loading="(!noLazy && 'lazy') || undefined"
      @load="onLoad"
      @error="(e) => onError(e, true)"
    />

    <span
      v-else
      class="placeholder"
    />
  </div>
</template>

<style scoped lang="postcss">
i,
svg {
  @apply opacity-25;
}
.image-wrapper {
  @apply flex h-full w-full items-center justify-center;
  &.rounded {
    @apply overflow-hidden rounded-md;
  }
  &:not(.transparent) {
    @apply bg-gray-100;
  }

  &.image-loading {
    opacity: 0;
  }
  &.image-error {
    opacity: 0;
    display: none;
  }
  &.image-ready {
    opacity: 1;
  }

  &:not(.no-transition) {
    @apply transition-all duration-200;
  }
}
picture,
img {
  @apply h-full w-full object-cover object-center;

  &.contain {
    @apply object-contain;
  }
}
.placeholder {
  @apply block h-full w-full;
}
</style>
